export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageProcessingOptions {
  cropArea?: CropArea;
  zoom?: number;
  targetWidth?: number;
  targetHeight?: number;
  quality?: number;
}

/**
 * Creates a canvas element with the specified dimensions
 */
const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/**
 * Loads an image from a URL or file
 */
export const loadImage = (src: string | File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    
    if (src instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(src);
    } else {
      img.src = src;
    }
  });
};

/**
 * Crops an image based on the provided crop area
 */
export const cropImage = (
  image: HTMLImageElement,
  cropArea: CropArea,
  zoom: number = 1
): HTMLCanvasElement => {
  const canvas = createCanvas(cropArea.width, cropArea.height);
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Unable to get canvas context');
  }

  // Calculate the actual crop area considering zoom
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  
  const actualCropArea = {
    x: cropArea.x * scaleX,
    y: cropArea.y * scaleY,
    width: cropArea.width * scaleX,
    height: cropArea.height * scaleY
  };

  ctx.drawImage(
    image,
    actualCropArea.x,
    actualCropArea.y,
    actualCropArea.width,
    actualCropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  );
  
  return canvas;
};

/**
 * Resizes an image to the specified dimensions
 */
export const resizeImage = (
  canvas: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number
): HTMLCanvasElement => {
  const resizedCanvas = createCanvas(targetWidth, targetHeight);
  const ctx = resizedCanvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Unable to get canvas context');
  }

  // Use smooth scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
  
  return resizedCanvas;
};

/**
 * Converts canvas to JPG format with specified quality
 */
export const canvasToJpg = (
  canvas: HTMLCanvasElement,
  quality: number = 0.9
): string => {
  return canvas.toDataURL('image/jpeg', quality);
};

/**
 * Gets the file extension from a file name or MIME type
 */
export const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

/**
 * Determines if a file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Gets the MIME type from a data URL
 */
export const getMimeTypeFromDataUrl = (dataUrl: string): string => {
  const matches = dataUrl.match(/^data:([^;]+);/);
  return matches ? matches[1] : '';
};

/**
 * Main function to process an image with all options
 */
export const processImage = async (
  imageSource: string | File,
  options: ImageProcessingOptions = {}
): Promise<string> => {
  const {
    cropArea,
    zoom = 1,
    targetWidth = 512,
    targetHeight = 512,
    quality = 0.9
  } = options;

  try {
    // Load the image
    const image = await loadImage(imageSource);
    
    let canvas: HTMLCanvasElement;
    
    if (cropArea) {
      // Crop the image first
      canvas = cropImage(image, cropArea, zoom);
    } else {
      // Create a canvas with the full image
      canvas = createCanvas(image.naturalWidth, image.naturalHeight);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Unable to get canvas context');
      }
      ctx.drawImage(image, 0, 0);
    }
    
    // Resize to target dimensions
    const resizedCanvas = resizeImage(canvas, targetWidth, targetHeight);
    
    // Convert to JPG
    return canvasToJpg(resizedCanvas, quality);
  } catch (error) {
    throw new Error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Validates image file size and dimensions
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check if it's an image
  if (!isImageFile(file)) {
    return { valid: false, error: 'File must be an image' };
  }
  
  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  
  return { valid: true };
};

/**
 * Creates a preview URL for a processed image
 */
export const createImagePreview = async (
  file: File,
  cropArea?: CropArea,
  zoom?: number
): Promise<string> => {
  return processImage(file, {
    cropArea,
    zoom,
    targetWidth: 128,
    targetHeight: 128,
    quality: 0.8
  });
}; 